import * as contract from '@tableau/preslayer-api-contract';
import { AddInInstancePresModel } from '../ApiCore';
import {
  AddInApiPresLayerInteropObject,
  Notification as PresLayerNotification,
  PlatformInfo,
} from './types/AddInApiPresLayerInteropObject';

export type NotificationHandler = (pm: contract.PresentationModel) => void;

/**
 * Implemenation of the PresentationLayer contract with a bit of extra information
 * added to work with add-ins. Most work is delegated down to the interopObject which
 * communicates directly with the c++ layer via QWebChannel
 *
 * @class AddInApiPresLayerImpl
 * @implements {contract.PresentationLayer}
 */
export class AddInApiPresLayerImpl implements contract.PresentationLayer {
  private _notificationHandlers: { [eventid: string]: [NotificationHandler] };

  public constructor(private _interopObject: AddInApiPresLayerInteropObject) {
    this._notificationHandlers = {};
    if (_interopObject && _interopObject.OnNotification) {
      _interopObject.OnNotification.connect((notification) => {
        this.dispatchNotification(notification);
      });
    }
  }

  /**
   * Gets the instance info for this particular add-in from the c++ code
   *
   * @readonly
   * @type {AddInInstancePresModel}
   * @memberof AddInApiPresLayerImpl
   */
  public get AddInInstanceInfo(): AddInInstancePresModel {
    return this._interopObject.addInInstanceInfo as AddInInstancePresModel;
  }

  /**
   * Gets the platform info from the c++ code
   *
   * @readonly
   * @type {PlatformInfo}
   * @memberof AddInApiPresLayerImpl
   */
  public get PlatformInfo(): PlatformInfo {
    return this._interopObject.platformInfo as PlatformInfo;
  }

  /**
   * Invokes a command using the pres-layer interop
   *
   * @template T - The expected return type
   * @param {string} commandNamespace
   * @param {string} commandName
   * @param {object} params
   * @returns {Promise<T>}
   * @memberof AddInApiPresLayerImpl
   */
  public invokeCommand<T>(commandNamespace: string, commandName: string, params: object): Promise<T> {
    commandNamespace = commandNamespace || 'tabdoc';
    return this._interopObject.ExecuteCommand<T>(commandNamespace, commandName, params).then((response) => {
      if (!response.Success) {
        const msg = `ExecuteCommand failed, with result: ${JSON.stringify(response.Error)}`;
        return Promise.reject<T>(msg);
      } else {
        // Since 2021.2/2.0.0 we receive result as string
        return JSON.parse(response.Result) as T;
      }
    });
  }

  // This implementation will registration a single instance of a notification handler with the Native C++ object,
  // and implement multi-dispatch to the web objects from here
  public registerNotificationHandler(eventId: string, handler: NotificationHandler): () => void {
    if (eventId in this._notificationHandlers) {
      this._notificationHandlers[eventId].push(handler);
    } else {
      this._notificationHandlers[eventId] = [handler];
      try {
        this._interopObject.RegisterNotificationHandler(eventId);
      } catch (err) {
        // do nothing
      }
    }
    return () => this.removeNotificationHandler(eventId, handler);
  }

  private removeNotificationHandler(eventId: string, handler: NotificationHandler): void {
    const handlerList = this._notificationHandlers[eventId];
    if (!handlerList) {
      return;
    }
    const foundIndex = handlerList.indexOf(handler);
    if (foundIndex >= 0) {
      handlerList.splice(foundIndex, 1);
    }
    if (handlerList.length < 1) {
      delete this._notificationHandlers[eventId];
    }
  }
  private dispatchNotification(notification: PresLayerNotification): void {
    const eventId = notification.eventId;
    const presModel = notification.presModel;
    if (eventId in this._notificationHandlers) {
      const handlers = this._notificationHandlers[eventId];
      for (let i = handlers.length - 1; i >= 0; i--) {
        handlers[i](presModel);
      }
    }
  }
}
